import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuHandler, MenuList, MenuItem } from '@material-tailwind/react';
import { Search, Bell, Menu as MenuIcon, User, Settings, LogOut } from 'lucide-react';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { ROUTES } from '@/utils/constants';

interface TopNavProps {
  className?: string;
  /** Mono section eyebrow shown on the left (e.g. "01 — Console"). */
  sectionLabel?: string;
  /** Syne 18px page title shown on the left. */
  title?: string;
}

/**
 * Top navigation bar — 64px tall, hairline bottom border, frosted (the one
 * sanctioned blur). Left: mono section label + Syne title. Right: mono search
 * well + bell + green avatar.
 */
export function TopNav({
  className = '',
  sectionLabel = 'Console',
  title = 'Threat feed',
}: TopNavProps): React.ReactElement {
  const navigate = useNavigate();
  const { toggleSidebar, notifications, openSearch } = useUIStore();
  const { user, clearAuth } = useAuthStore();

  const unreadNotifications = notifications.length;

  const handleLogout = () => {
    clearAuth();
    navigate(ROUTES.LOGIN);
  };

  const handleProfileClick = () => {
    navigate(ROUTES.PROFILE);
  };

  const getUserInitials = (): string => {
    if (!user?.full_name) return 'U';
    const names = user.full_name.split(' ');
    if (names.length >= 2 && names[0] && names[1]) {
      return `${names[0][0] ?? ''}${names[1][0] ?? ''}`.toUpperCase();
    }
    return names[0]?.[0]?.toUpperCase() ?? 'U';
  };

  return (
    <nav
      className={`sticky top-0 z-10 h-16 flex items-center gap-4 px-4 lg:px-8
        border-b border-phish-border bg-phish-bg-0/85 backdrop-blur-phish-nav ${className}`}
    >
      {/* Mobile sidebar toggle */}
      <button
        type="button"
        onClick={toggleSidebar}
        aria-label="Toggle sidebar"
        className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-phish
          text-phish-fg-2 hover:bg-white/5 transition-colors duration-phish"
      >
        <MenuIcon size={20} strokeWidth={1.5} />
      </button>

      {/* Left — section label + title */}
      <div className="flex min-w-0 flex-col justify-center">
        <span className="font-mono text-[11px] uppercase tracking-phish-label text-phish-fg-3">
          {sectionLabel}
        </span>
        <span className="font-display text-[18px] font-bold leading-tight tracking-phish-tight text-phish-fg-1 truncate">
          {title}
        </span>
      </div>

      {/* Right — search well, notifications, avatar */}
      <div className="ml-auto flex items-center gap-3">
        {/* Search well */}
        <button
          type="button"
          onClick={openSearch}
          className="hidden sm:flex items-center gap-2.5 h-9 px-3 w-64 lg:w-80
            bg-phish-bg-2 border border-phish-border rounded-phish font-mono text-[13px]
            text-phish-fg-3 hover:border-phish-border-strong transition-colors duration-phish"
        >
          <Search size={16} strokeWidth={1.5} className="text-phish-fg-3" />
          <span className="flex-1 text-left truncate">search detections…</span>
          <kbd className="hidden md:inline-flex items-center font-mono text-[11px] text-phish-fg-3
            border border-phish-border rounded-phish-0 px-1">
            ⌘K
          </kbd>
        </button>

        {/* Search (mobile icon) */}
        <button
          type="button"
          onClick={openSearch}
          aria-label="Search"
          className="sm:hidden inline-flex h-9 w-9 items-center justify-center rounded-phish
            text-phish-fg-2 hover:bg-white/5 transition-colors duration-phish"
        >
          <Search size={20} strokeWidth={1.5} />
        </button>

        {/* Notifications */}
        <Menu>
          <MenuHandler>
            <button
              type="button"
              aria-label="Notifications"
              className="relative inline-flex h-9 w-9 items-center justify-center rounded-phish
                text-phish-fg-2 hover:bg-white/5 transition-colors duration-phish"
            >
              <Bell size={20} strokeWidth={1.5} />
              {unreadNotifications > 0 && (
                <span
                  className="absolute top-1 right-1 inline-flex h-4 min-w-[16px] items-center justify-center
                    px-1 rounded-phish bg-phish-critical text-phish-bg-0 font-mono text-[10px] font-bold"
                >
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </button>
          </MenuHandler>
          <MenuList className="max-h-72 w-80 bg-phish-bg-1 border border-phish-border rounded-phish-lg shadow-none">
            <div className="px-3 py-2 font-mono text-[11px] uppercase tracking-phish-label text-phish-fg-3">
              Notifications
            </div>
            <hr className="my-1 border-phish-border" />
            {notifications.length === 0 ? (
              <MenuItem disabled className="font-sans text-[13px] text-phish-fg-3">
                No new notifications
              </MenuItem>
            ) : (
              notifications.slice(0, 5).map((notification) => (
                <MenuItem
                  key={notification.id}
                  className="flex flex-col items-start gap-1 hover:bg-white/5"
                >
                  <span className="font-mono text-[12px] uppercase tracking-phish-label text-phish-fg-2">
                    {notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}
                  </span>
                  <span className="font-sans text-[13px] text-phish-fg-2">
                    {notification.message}
                  </span>
                </MenuItem>
              ))
            )}
          </MenuList>
        </Menu>

        {/* User menu — green square avatar */}
        <Menu placement="bottom-end">
          <MenuHandler>
            <button
              type="button"
              className="inline-flex h-[34px] w-[34px] items-center justify-center
                rounded-phish bg-phish-accent text-phish-bg-0 font-mono text-[13px] font-bold
                transition-opacity duration-phish active:opacity-85"
              aria-label={user?.full_name || 'User'}
            >
              {getUserInitials()}
            </button>
          </MenuHandler>
          <MenuList className="bg-phish-bg-1 border border-phish-border rounded-phish-lg shadow-none">
            <div className="px-3 py-2 border-b border-phish-border mb-1">
              <div className="font-sans text-[13px] text-phish-fg-1 truncate">
                {user?.full_name || 'User'}
              </div>
              <div className="font-mono text-[11px] capitalize text-phish-fg-3">
                {user?.role || 'viewer'}
              </div>
            </div>
            <MenuItem
              className="flex items-center gap-2 font-sans text-[13px] text-phish-fg-2 hover:bg-white/5"
              onClick={handleProfileClick}
            >
              <User size={16} strokeWidth={1.5} />
              My Profile
            </MenuItem>
            <MenuItem
              className="flex items-center gap-2 font-sans text-[13px] text-phish-fg-2 hover:bg-white/5"
              onClick={() => navigate(ROUTES.COMPANY_SETTINGS)}
            >
              <Settings size={16} strokeWidth={1.5} />
              Settings
            </MenuItem>
            <hr className="my-1 border-phish-border" />
            <MenuItem
              className="flex items-center gap-2 font-sans text-[13px] text-phish-critical hover:bg-white/5"
              onClick={handleLogout}
            >
              <LogOut size={16} strokeWidth={1.5} />
              Logout
            </MenuItem>
          </MenuList>
        </Menu>
      </div>
    </nav>
  );
}

export default TopNav;
